/*
 * Redux storage service, inspired by reduxie project.
 */

import { MiddlewareAPI, Dispatch, Action } from 'redux';
import Dexie from 'dexie';
import { ApplicationState } from '../store';

const dbName = 'LogStackPro';
const deleteCount = 4;

interface IReduxState {
  reduxieId?: number;
  snapshot: unknown;
}

class Reduxie extends Dexie {
  state: Dexie.Table<IReduxState, number>;
  constructor(dbName: string) {
    super(dbName);
    this.version(1).stores({
      state: '++reduxie_id',
    });
    this.state = this.table('state');
  }
}

let queuedState: Record<string, unknown> | undefined;

export class ReduxieService {
  static async loadState(): Promise<ApplicationState> {
    const db = new Reduxie(dbName);
    await db.open();
    const rec = await db.table('state').toCollection().last();
    delete rec.reduxie_id;
    return rec;
  }

  static async saveQueued() {
    if (queuedState) {
      const state = queuedState;
      queuedState = undefined;

      const db = new Reduxie(dbName);
      const count = await db.table('state').count();
      if (count >= deleteCount) {
        // check if IDB storage has reached limit
        // console.debug('going to clear then add');
        await db.table('state').clear();
        return db.table('state').add(state);
      } else {
        return db.table('state').add(state);
      }
    }
  }

  static clear(): Promise<void> {
    queuedState = undefined;
    const db = new Reduxie(dbName);
    return db.table('state').clear();
  }

  static middleware(whitelist: string[]) {
    return ({ getState }: MiddlewareAPI<Dispatch<Action>, ApplicationState>) =>
      (next: Dispatch<Action>) =>
      (action: Action) => {
        // Immediately dispatch action to middleware/reducers
        next(action);

        const state = getState();
        const filtered: Record<string, unknown> = {};
        for (const key of whitelist) {
          const item = state[key as keyof ApplicationState];
          if (item) {
            filtered[key] = item;
          }
        }
        queuedState = filtered;
      };
  }
}

window.setInterval(ReduxieService.saveQueued, 1000);
